import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Instagram } from "lucide-react";
import logo from "@/assets/logo.png";

const links = [
  { label: "About Us", id: "manifesto" },
  { label: "Events", id: "events" },
  { label: "Gallery", id: "gallery" },
  { label: "Past Shows", id: "past-shows" },
  { label: "Merch", id: "merch" },
];

const IG_URL = "https://www.instagram.com/speedsocialevents";

function useScrolled(threshold = 40) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

function InstagramLink({ testId, className }) {
  return (
    <a href={IG_URL} target="_blank" rel="noopener noreferrer" data-testid={testId} aria-label="Instagram @speedsocialevents" className={className}>
      <Instagram className="h-4 w-4" />
      <span className="font-body text-xs uppercase tracking-widest">@speedsocialevents</span>
    </a>
  );
}

function Logo({ onClick }) {
  return (
    <button data-testid="logo-button" onClick={onClick} className="group flex items-center gap-3">
      <img src={logo} alt="Speed Social Events logo" className="h-14 w-auto brightness-0 invert transition-transform duration-500 group-hover:scale-105 md:h-16" />
      <span className="font-display text-lg font-extrabold tracking-tight text-white">
        SPEED<span className="text-white/40"> SOCIAL</span>
      </span>
    </button>
  );
}

function DesktopNav({ go }) {
  return (
    <nav className="hidden items-center gap-9 md:ml-16 md:flex lg:ml-24">
      {links.map((l) => (
        <button
          key={l.id}
          data-testid={`nav-${l.id}`}
          onClick={() => go(l.id)}
          className="group relative font-body text-sm uppercase tracking-widest text-white/60 transition-colors duration-300 hover:text-white"
        >
          {l.label}
          <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
        </button>
      ))}
    </nav>
  );
}

function MenuToggle({ open, onToggle }) {
  return (
    <button data-testid="menu-toggle" onClick={onToggle} className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 md:hidden">
      <span className={`h-px w-6 bg-white transition-transform duration-300 ${open ? "translate-y-[7px] rotate-45" : ""}`} />
      <span className={`h-px w-6 bg-white transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
      <span className={`h-px w-6 bg-white transition-transform duration-300 ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
    </button>
  );
}

function HeaderActions({ onReserve, menuOpen, onToggleMenu }) {
  return (
    <div className="flex items-center gap-3">
      <InstagramLink testId="header-instagram" className="hidden items-center gap-2 text-white/60 transition-colors duration-300 hover:text-primary md:ml-8 md:flex lg:ml-12" />
      <button data-testid="header-reserve-button" onClick={onReserve} className="group relative hidden overflow-hidden border border-white/20 px-6 py-2.5 md:block">
        <span className="relative z-10 font-body text-xs font-medium uppercase tracking-widest text-white transition-colors duration-300 group-hover:text-white">
          Reserve
        </span>
        <span className="absolute inset-0 -translate-x-full bg-primary transition-transform duration-300 group-hover:translate-x-0" />
      </button>
      <MenuToggle open={menuOpen} onToggle={onToggleMenu} />
    </div>
  );
}

function MobileMenu({ open, go, onReserve }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="mobile-menu"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl md:hidden"
        >
          <div className="flex flex-col px-6 py-6">
            {links.map((l) => (
              <button key={l.id} data-testid={`mobile-nav-${l.id}`} onClick={() => go(l.id)} className="border-b border-white/5 py-4 text-left font-display text-2xl font-bold text-white">
                {l.label}
              </button>
            ))}
            <button data-testid="mobile-reserve-button" onClick={onReserve} className="mt-6 bg-primary py-4 font-body text-sm uppercase tracking-widest text-white">
              Reserve Your Spot
            </button>
            <InstagramLink testId="mobile-instagram" className="mt-6 flex items-center gap-2 text-white/70" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Header({ onReserve }) {
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);
  const lenis = useLenis();

  const go = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollTop = () => {
    if (lenis) lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const mobileReserve = () => {
    setMenuOpen(false);
    onReserve();
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 z-50 w-full transition-[background-color,border-color] duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-10">
        <Logo onClick={scrollTop} />
        <DesktopNav go={go} />
        <HeaderActions onReserve={onReserve} menuOpen={menuOpen} onToggleMenu={() => setMenuOpen((v) => !v)} />
      </div>
      <MobileMenu open={menuOpen} go={go} onReserve={mobileReserve} />
    </motion.header>
  );
}
